import { RemoteWidgetFormProps } from '@highimpact-front/remote-widget-core';
import {
  ApiDashboardElement,
  ApiRemoteWidget,
  JSONSerializable,
} from '@highimpact-frontend/api-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDashboardHost } from '../../../../context/DashboardHostContext';
import type { MigratedPlotFormProps } from '../../../../forms/formHandlerShared';
import { buildRemoteManifestUrl } from '../../../../internal/remote/buildRemoteManifestUrl';
import {
  finishRemoteWidgetConfiguration,
  getAdditionalProperties,
  getAdditionalPropertiesForRemotePlugin,
  getName,
  getSpecificWidgetType,
  getUrl,
  isValid,
  resolveRemoteWidgetConfigForSubmit,
  sanitizeRemoteWidgetConfigForPlugin,
  setAdditionalProperties,
  setName,
  setSpecificWidgetType,
  stripAwaitingConfigurationProperty,
} from '../../../../state/dashboard-element-operations';
import {
  PlotConfigFormDivider,
  PlotConfigFormShell,
  PlotConfigNameField,
} from '../shared/plotConfigFormLayout';

function RemoteWidgetFormHelper({
  getSpecificWidgetType,
  config,
  url,
  ...props
}: RemoteWidgetFormProps & MigratedPlotFormProps & { url: string }) {
  const host = useDashboardHost();
  const specificWidgetType = getSpecificWidgetType(config);
  if (specificWidgetType === undefined) {
    return <p>Type d'élément non trouvé</p>;
  }
  const remoteURL = buildRemoteManifestUrl(url, specificWidgetType);
  return (
    <>
      {host.renderRemoteWidget({
        remoteName: specificWidgetType,
        remoteUrl: remoteURL,
        moduleName: 'Form',
        props: { ...props, getSpecificWidgetType, config },
      })}
    </>
  );
}

function RemoteWidgetForm({
  config,
  project_id,
  site_id,
  campaigns,
  start_time,
  end_time,
  elementTypes,
  onChange,
  onSubmit,
  mfeMetaData,
}: MigratedPlotFormProps) {
  const remoteElements: Record<string, [string, string]> = Object.fromEntries(
    mfeMetaData
      ? mfeMetaData.map((mfe) => [
          mfe.name,
          [mfe.pretty_name, mfe.url] as [string, string],
        ])
      : []
  );

  const [submitCount, setSubmitCount] = useState(0);
  const [validation, setValidation] = useState(isValid(config));
  const latestConfigRef = useRef(config);
  const pluginConfig = sanitizeRemoteWidgetConfigForPlugin(
    config
  ) as ApiRemoteWidget;

  useEffect(() => {
    latestConfigRef.current = config;
    setValidation(isValid(config));
  }, [config]);

  const handleConfigChange = (newConfig: ApiDashboardElement) => {
    latestConfigRef.current = finishRemoteWidgetConfiguration(newConfig);
    setValidation(isValid(latestConfigRef.current));
    onChange(latestConfigRef.current);
  };

  const setAdditionalPropertiesForPlugin = (
    additionalProps: JSONSerializable,
    element: ApiDashboardElement
  ): ApiDashboardElement =>
    setAdditionalProperties(
      stripAwaitingConfigurationProperty(additionalProps) ?? null,
      element
    );

  const submitConfig = useCallback(
    (configOverride?: ApiDashboardElement) => {
      const resolved = resolveRemoteWidgetConfigForSubmit(
        latestConfigRef.current,
        configOverride
      );
      const configToSubmit = finishRemoteWidgetConfiguration(resolved);
      setSubmitCount((count) => count + 1);
      if (isValid(configToSubmit)[0]) {
        return onSubmit(configToSubmit);
      }
      setValidation(isValid(configToSubmit));
      return Promise.resolve();
    },
    [onSubmit]
  );

  const handleNameChange = (name: string) => {
    handleConfigChange(setName(name, config));
  };

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    void submitConfig();
  };

  const url = getUrl(config);

  if (url === undefined || getSpecificWidgetType(config) === '') {
    return <p>URL not found for this plugin</p>;
  }

  return (
    <PlotConfigFormShell onSubmit={handleSubmit}>
      <PlotConfigNameField
        value={getName(config)}
        onChange={handleNameChange}
        errors={validation[1].name ?? []}
        submitted={submitCount > 0}
        required
      />

      <PlotConfigFormDivider />

      <RemoteWidgetFormHelper
        mfeMetaData={mfeMetaData}
        remoteElements={Object.entries(remoteElements).map(([type]) => type)}
        config={pluginConfig}
        url={url}
        project_id={project_id}
        site_id={site_id}
        campaigns={campaigns}
        start_time={start_time}
        end_time={end_time}
        elementTypes={elementTypes}
        getSpecificWidgetType={getSpecificWidgetType}
        setSpecificWidgetType={setSpecificWidgetType}
        getAdditionalProperties={(element) => {
          const raw = getAdditionalProperties(element) as
            | JSONSerializable
            | null
            | undefined;
          const forPlugin = getAdditionalPropertiesForRemotePlugin(raw);
          return forPlugin ?? undefined;
        }}
        setAdditionalProperties={setAdditionalPropertiesForPlugin}
        onChange={handleConfigChange}
        onSubmit={submitConfig}
      />

      <PlotConfigFormDivider />
    </PlotConfigFormShell>
  );
}

export default RemoteWidgetForm;
